import { DeviationRequestDetail, DeviationRequestDetailApi, DeviationRequestHistoryRecord, DeviationRequestPermissions } from 'api/deviation-request-detail';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { EventHelper } from 'framework/aurelia_tools/event-helper';
import { log } from 'framework/script_utilities/dev-decorators';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { DeviationDetailType, DeviationType } from 'model/business-enums';
import { copyDeviationRequest, DeviationRequest, DeviationRequestWorkflowStrategy, DeviationSettings, EntityWideDeviationRequest, initDeviationRequest, SingleInstanceDeviationRequest } from 'model/forms/deviation-request';
import { TravelRequest } from 'model/forms/travel-request';
import { EnumsService } from 'services/enums-service';
import { IdentityService } from 'services/identity-service';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { SafetyService } from 'services/safety-service';
import { requireServices } from 'support/activation';
import { DataTransferer } from 'support/shared_models/data-transferer';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';
import { DeviationRequestStatus } from 'api/swagger/ApiClient';
import { HOME_ROUTE } from 'support/settings';
import { DeviationRequestAssignToItem } from 'components/deviation-request-assign-to-button';
import { RequestAttachmentsController } from 'components/request-attachments-panel';
import { FileParameter, UploadedFile } from 'model/essential-types';
import { downloadFile } from 'framework/script_utilities/file-utils';
import { FilesUploadController } from 'framework/script_utilities/files-upload-controller';
import { ErrorInfo } from '../../framework/error_handling/error-handler';
import { fileSizeToView } from '../../framework/script_utilities/text-utils';
import { DeviationRequestsService } from 'services/deviation-requests-service';

@autoinject
export class DeviationRequestDetailScreen {
  id: number = null;
  copyFromId: number = null;
  route: RouteConfig = null;
  model: DeviationRequest = null;
  permissions: DeviationRequestPermissions = null;
  workflowStrategy: DeviationRequestWorkflowStrategy = null;
  history: DeviationRequestHistoryRecord[] = null;
  backup: DeviationRequestDetail = null;
  loaded = false;
  editing = false;
  attachmentEditing = false;
  changed = false;
  bindSingleInstance = false;
  bindEntityWide = false;
  initialDeviationSettings: DeviationSettings = null;
  copyFromRequest: DeviationRequestDetail = null;
  assignToItems: DeviationRequestAssignToItem[] = [];
  attachmentsController: RequestAttachmentsController = null;

  constructor(private identity: IdentityService, private keywords: KeywordsService, private enums: EnumsService, private lookups: LookupsService, private safety: SafetyService, private api: DeviationRequestDetailApi, private router: Router, private transferer: DataTransferer, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, private eventHelper: EventHelper, @newInstance() private validationController: ValidationController, @newInstance() private supportValidationController: ValidationController, private validationRenderer: BootstrapGroupRenderer, @newInstance() private filesUploadController: FilesUploadController, private deviationRequestsService: DeviationRequestsService) {
    this.validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup, bs-form-select, bs-form-textarea';
    this.validationController.addRenderer(this.validationRenderer);
    this.attachmentsController = {
      attachments: [],
      maxAllowedAttachmensCount: this.deviationRequestsService.maxAllowedAttachmensCount,
      allowedFileExtensions: this.deviationRequestsService.allowedFileExtensions,
      filesUploadController: this.filesUploadController,
      maximumAllowedAttachmentsExceeded: () => {
        this.toastHelper.error({ title: 'File upload failed', message: `Cannot upload a new attachment as there can be maximum ${this.deviationRequestsService.maxAllowedAttachmensCount} attachments per Deviation Request.` } as ErrorInfo);
      },
      upload: (attachment: FileParameter) => {
        const fileExtension = `.${attachment.fileName.split('.').pop()}`;
        if (attachment.data.size > this.deviationRequestsService.maxFileSizeInBytes) {
          this.toastHelper.error({ title: 'File upload failed', message: `Selected file ${attachment.fileName} is too big. Please upload files with maximum size of ${fileSizeToView(this.deviationRequestsService.maxFileSizeInBytes)}.` } as ErrorInfo);
          return Promise.reject();
        }
        else if (!this.deviationRequestsService.allowedFileExtensions.some(x => x === fileExtension)) {
          this.toastHelper.error({ title: 'File upload failed', message: `Type of selected file ${attachment.fileName} is not supported. Please select supported file type.` } as ErrorInfo);
          return Promise.reject();
        }
        else {
          return this.api.uploadAttachment(this.id, attachment)
            .then(x => {
              this.model.attachments.push(x);
              return x;
            })
            .catch((reason) => {
              this.toastHelper.error({ title: 'File upload failed', message: `Uploading file ${attachment.fileName} has failed. Please try to upload the file again.`, details: reason } as ErrorInfo);
              return Promise.reject(reason);
            });
        }
      },
      download: (attachment: UploadedFile) => {
        return this.api.downloadAttachment(this.id, attachment).then(x => {
          const url = URL.createObjectURL(x.data);
          try { downloadFile(url, `${attachment.fileName}${attachment.extension}`); }
          finally { URL.revokeObjectURL(url); }
        });
      },
      remove: (attachment: UploadedFile) => {
        this.model.attachments.splice(this.model.attachments.indexOf(attachment), 1);
      }
    } as RequestAttachmentsController;
  }

  async canActivate() {
    await requireServices(this.identity, this.enums, this.keywords, this.lookups, this.safety);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.id = parseInt(params?.id) || null;
    this.copyFromId = parseInt(params?.copyFromId) || null;
    this.route = route;
    this.initialDeviationSettings = this.transferer.get('INITIAL-DEVIATION-SETTINGS') ?? null;
  }

  bind() {
    this.initialize();
  }

  entityWideWarning = `<p>While deviations from the Group Travel Policy are not normally permitted, there is recognition of certain special business needs that warrant a departure from the Policy.</p><p>Entity-wide deviations shall require approval and endorsement from:</p><ul><li>The bp Entity Leader relevant to that bp traveller’s business area</li><li>STC approval from the relevant SME for the intended area of the policy requiring a deviation (Health, Aviation / Logistics / Security)</li><li>The CFO as the issuing authority of this Policy</li></ul><p>A request for a deviation shall be completed by the requesting entity or individual. The request for a deviation shall include:</p><ul><li>A justification for the deviation based on the completion of a risk assessment</li><li>An action plan documenting the risk reduction measures that are to be applied</li></ul><p>Before you proceed with this entity-wide deviation, please ensure you have understood these requirements.</p>`;

  private async initialize() {
    this.copyFromRequest = this.copyFromId != null ? await this.api.getDeviationRequest(this.copyFromId) : null;
    await this.bindDetail(this.id ? await this.api.getDeviationRequest(this.id) : null);
    this.editing = (this.id && this.permissions.canEdit && this.autoEdit) || !this.id;
    this.attachmentEditing = (this.id && this.permissions.canEditAttachment && this.autoEdit) || !this.id;
    this.bindSingleInstance = this.model.deviationType === DeviationType.SingleInstance;
    this.bindEntityWide = this.model.deviationType === DeviationType.EntityWide;
    this.loaded = true;
    if (this.bindEntityWide && !this.id)
      this.dialogHelper.alert({ message: this.entityWideWarning, dialogTitle: 'Entity-Wide Deviation', okTitle: 'Understood', class: '--warning', icon: 'fas fa-hand-paper' });
  }

  async canDeactivate() {
    if ((this.editing || this.attachmentEditing) && this.changed && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the request yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  @log('debug')
  private async bindDetail(detail?: DeviationRequestDetail) {
    this.id = detail?.data.id ?? null;
    if (this.model != null)
      this.supportValidationController.removeObject(this.model);
    this.supportValidationController.removeRenderer(this.validationRenderer);
    this.supportValidationController.addRenderer(this.validationRenderer);
    this.model = detail?.data ?? this.getEmptyForm();
    this.workflowStrategy = detail?.workflowStrategy ?? await this.api.getWorkflowStrategy(this.model.deviationType, this.model.workflowType);
    this.attachmentsController.attachments = this.model.attachments;
    this.permissions = detail?.permissions ?? null;
    this.assignToItems = [];
    if (this.permissions !== null) {
      if (this.model.assignedTo !== null)
        this.assignToItems.push({ name: '-- unassign --', onClick: () => this.assignTo(null) });
      this.assignToItems.push(...this.permissions.teamMembersToAssign.filter(x => this.model.assignedTo === null || x.id !== this.model.assignedTo.id).map(x => ({ name: x.name, onClick: () => this.assignTo(x.id) } as DeviationRequestAssignToItem)));
    }
    this.history = detail?.history ?? null;
    this.backup = cloneDeep(detail);
  }

  private get autoEdit() {
    return false;
  }

  assignToButtonVisible = false;
  editButtonClicked = false;
  editButtonVisible = false;
  supportByRegionDirectorButtonVisible = false;
  supportButtonVisible = false;
  rejectButtonVisible = false;
  renewButtonVisible = false;
  archiveButtonVisible = false;
  restoreButtonVisible = false;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  @watch((x: DeviationRequestDetailScreen) => x.model)
  @watch((x: DeviationRequestDetailScreen) => x.permissions)
  @watch((x: DeviationRequestDetailScreen) => x.editing)
  @watch((x: DeviationRequestDetailScreen) => x.attachmentEditing)
  @watch((x: DeviationRequestDetailScreen) => x.changed)
  @watch((x: DeviationRequestDetailScreen) => x.editButtonClicked)
  private updateButtons() {
    this.assignToButtonVisible = !this.editing && !this.attachmentEditing && this.permissions?.canAssign;
    this.editButtonVisible = !this.editing && !this.attachmentEditing && (this.permissions?.canEdit || this.permissions?.canEditAttachment);
    this.supportByRegionDirectorButtonVisible = !this.editing && !this.attachmentEditing && this.permissions?.canSupportByRegionDirector;
    this.supportButtonVisible = !this.editing && !this.attachmentEditing && this.permissions?.canSupport;
    this.rejectButtonVisible = !this.editing && !this.attachmentEditing && this.permissions?.canReject;
    this.renewButtonVisible = !this.editing && !this.attachmentEditing && this.permissions?.canRenew;
    this.archiveButtonVisible = !this.editing && !this.attachmentEditing && this.permissions?.canArchive;
    this.restoreButtonVisible = !this.editing && !this.attachmentEditing && this.permissions?.canRestore;
    this.submitButtonVisible = (this.editing || this.attachmentEditing) && this.changed;
    this.cancelButtonVisible = (this.editing || this.attachmentEditing) && (this.changed || this.editButtonClicked);
  }

  async edit(editToSupport: boolean) {
    if (!editToSupport)
      this.supportValidationController.removeRenderer(this.validationRenderer);
    if (this.permissions?.canEdit) {
      this.editing = true;
    }
    if (this.permissions?.canEditAttachment) {
      this.attachmentEditing = true;
    }
    this.editButtonClicked = true;
  }

  async renew() {
    this.transferer.set('INITIAL-DEVIATION-SETTINGS', { deviationType: null, workflowType: null, detailType: null, prepopulatedFields: null } as DeviationSettings);
    this.router.navigate(`#/${HOME_ROUTE}/deviationRequests/new/${this.id}`);
  }

  async supportByRegionDirector() {
    if (await this.validationHelper.validate(this.supportValidationController)) {
      const onBehalfOfRegionDirector = this.model.regionDirector.director.uniqueid !== this.identity.user.uniqueid;
      if (await this.dialogHelper.confirm({ dialogTitle: 'Support the Deviation Request?', message: `Please confirm you want to support the Deviation Request${(onBehalfOfRegionDirector ? " on behalf of Region Director" : "")}.`, okTitle: 'Sure, support it!', cancelTitle: 'Back to form' })) {
        await this.bindDetail(await this.api.changeStatus(this.model.id, DeviationRequestStatus.SupportedByRegionDirector));
        this.toastHelper.toast('Deviation request is <b>supported</b> by Region Director.', 'Supported by Region Director', ToastType.Success, ToastFormat.Html);
        if (onBehalfOfRegionDirector)
          await this.dialogHelper.alert("The Deviation Request must be now supported by the Team Lead.", "Team Warning.");
      }
    }
    else {
      await this.edit(true);
    }
  }

  async support() {
    if (await this.validationHelper.validate(this.supportValidationController)) {
      if (await this.dialogHelper.confirm({ dialogTitle: 'Support the Deviation Request?', message: 'Please confirm you want to support the Deviation Request.', okTitle: 'Sure, support it!', cancelTitle: 'Back to form' })) {
        await this.bindDetail(await this.api.changeStatus(this.model.id, DeviationRequestStatus.Supported));
        this.toastHelper.toast('Deviation request is <b>supported</b>.', 'Supported', ToastType.Success, ToastFormat.Html);
      }
    }
    else {
      await this.edit(true);
    }
  }

  async reject() {
    if (await this.dialogHelper.confirm({ dialogTitle: 'Reject the Deviation Request?', message: 'Please confirm you want to reject the Deviation Request.', okTitle: 'Sure, reject it!', cancelTitle: 'Back to form' })) {
      await this.bindDetail(await this.api.changeStatus(this.model.id, DeviationRequestStatus.Rejected));
      this.toastHelper.toast('Deviation request has been <b>rejected</b>.', 'Rejected', ToastType.Success, ToastFormat.Html);
    }
  }

  async submit() {
    (this.editing ? await this.validationHelper.validate(this.validationController) : true) && await this.filesUploadController.waitForUpload() && await this.save();
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the request yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      await this.bindDetail(this.backup);
      this.editing = !this.editButtonClicked;
      this.attachmentEditing = !this.editButtonClicked;
    }
  }

  async assignTo(assignToId: number | null) {
    await this.bindDetail(await this.api.assignTo(this.id, assignToId));
    this.toastHelper.toast(`Deviation request has been <b>${assignToId !== null ? 'assigned' : 'unassigned'}</b>.`, assignToId !== null ? 'Assigned' : 'Unassigned', ToastType.Success, ToastFormat.Html);
  }

  async archive() {
    await this.bindDetail(await this.api.archiveDeviationRequest(this.id));
    this.toastHelper.toast('Deviation request has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
    this.editing = this.editing && this.permissions.canEdit;
  }

  async restore() {
    await this.bindDetail(await this.api.restoreDeviationRequest(this.id));
    this.toastHelper.toast('Deviation request has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
    this.editing = this.permissions.canEdit && this.autoEdit;
  }

  private async save() {
    let response: DeviationRequestDetail | null = null;
    if (!this.editing && this.attachmentEditing) {
      response = await this.api.updateDeviationRequestAttachment(this.model.id, this.model);
    } else if (this.editing) {
      if (this.model.deviationType === DeviationType.SingleInstance) {
        const data = this.getSingleInstanceSaveData(this.model);
        response = this.id ? await this.api.updateSingleInstanceTravelRequest(this.id, data) : await this.api.createSingleInstanceTravelRequest(data);
      }
      else if (this.model.deviationType === DeviationType.EntityWide) {
        const data = this.getEntityWideSaveData(this.model);
        response = this.id ? await this.api.updateEntityWideTravelRequest(this.id, data) : await this.api.createEntityWideTravelRequest(data);
      }
    }
    await this.bindDetail(response);
    this.toastHelper.toast('Deviation request has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
    this.editing = false;
    this.attachmentEditing = false;
  }

  getSingleInstanceSaveData(model: DeviationRequest): SingleInstanceDeviationRequest {
    return model as SingleInstanceDeviationRequest;
  }

  getEntityWideSaveData(model: DeviationRequest): EntityWideDeviationRequest {
    return model as EntityWideDeviationRequest;
  }

  getSaveData(model: TravelRequest): TravelRequest {
    const data = cloneDeep(model);
    data.businessDetails.businessContacts = data.businessDetails.businessContacts.filter(r => r.name != null || r.phone != null || r.mail != null);
    return data;
  }

  getEmptyForm(): DeviationRequest {
    if (this.copyFromRequest !== null)
      return copyDeviationRequest(this.copyFromRequest.data, this.identity.user);
    else
      return initDeviationRequest(this.initialDeviationSettings, this.identity.user);
  }

  @computedFrom('id', 'model')
  get title() {
    const wokrflowName = this.model !== null && this.model.deviationType === DeviationType.EntityWide && this.model.detail.detailType === DeviationDetailType.ClassOfTravel ? this.enums.deviationWorkflowTypes.find(x => x.value === this.model.workflowType).name : '';
    return [this.id == null ? 'New' : '', this.model !== null ? `${this.enums.deviationTypes.find(x => x.value === this.model.deviationType).name} ${this.enums.deviationDetailTypes.find(x => x.value === this.model.detail.detailType).name} Deviation Request${wokrflowName !== '' ? (` (${wokrflowName})`) : ''}` : 'Deviation Request'].filter(p => p).join(' ');
  }

  @computedFrom('editButtonVisible', 'supportButtonVisible', 'rejectButtonVisible', 'archiveButtonVisible', 'restoreButtonVisible', 'submitButtonVisible', 'cancelButtonVisible')
  get headerButtonsVisible() {
    return this.editButtonVisible || this.supportButtonVisible || this.rejectButtonVisible || this.archiveButtonVisible || this.restoreButtonVisible || this.submitButtonVisible || this.cancelButtonVisible;
  }

  @watch((x: DeviationRequestDetailScreen) => x.model)
  private modelChanged() {
    this.route.navModel.setTitle(this.title);
    this.validationController.reset();
  }

  @computedFrom('permissions.canEdit', 'permissions.canEditAttachment')
  get editButtonLabel(): string {
    return this.permissions.canEdit ? 'Edit Request' : (this.permissions.canEditAttachment ? 'Edit Attachments' : '');
  }
}
